import { Callback } from "game-common/models"
import { BitmapText, Graphics } from "pixi.js"
import { TextRenderer } from "../text/text_renderer"

interface ButtonProps {
    text: string
    font: string
    color: number
    textColor?: number
    buttonWidth?: number
    // Inner padding (px) around the label; also drives the button's vertical size. Defaults to 3.
    padding?: number
    onClick?: Callback
    mouseControlCallback?: (on: boolean) => void
}
export class Button extends Graphics {
    text: string
    font: string
    color: number
    textColor: number
    buttonWidth: number
    onClick?: Callback
    mouseControlCallback?: (on: boolean) => void

    // True while the pointer is on the button and mouseControlCallback has taken the world's mouse
    // away. Tracked so the latch can be handed back when the button goes (see releaseMouseControl),
    // and so a button never clears a latch it did not set -- it is one global boolean, and something
    // else (an open modal, another control under the pointer) may be holding it for a good reason.
    private holdingMouseControl: boolean = false

    constructor({ text, font, color, textColor, buttonWidth, padding: paddingProp, mouseControlCallback, onClick }: ButtonProps) {
        super()
        this.text = text
        this.font = font
        this.color = color
        this.textColor = textColor || 0x000000
        this.buttonWidth = buttonWidth
        this.mouseControlCallback = mouseControlCallback
        this.onClick = onClick

        const button = new Graphics()
        const textObj = new TextRenderer().color(this.textColor).align("center").render(this.text)
        const padding = paddingProp ?? 3
        textObj.x = buttonWidth ? buttonWidth / 2 - textObj.width / 2 : padding
        textObj.y = padding

        button.lineStyle(2, color, 1.0)
        button.beginFill(color, 1.0)
        button.drawRoundedRect(0, 0, buttonWidth || textObj.width + padding * 3, textObj.height + padding * 2, 5)
        button.addChild(textObj)

        this.drawRect(0, 0, this.buttonWidth || 1, button.height + padding)

        this.addChild(button)

        this.interactive = true

        if (this.mouseControlCallback) {
            this.on("mouseover", () => {
                this.alpha = 0.7
                this.setMouseControl(true)
            })
            this.on("mouseout", () => {
                this.alpha = 1.0
                this.setMouseControl(false)
            })
            // pixi delivers no mouseout to a display object that has been removed from the display
            // tree, so a button taken down under a resting pointer would hold the world's mouse for
            // good -- and these buttons do exactly that: they throb and expire on a timer, and the
            // message button rebuilds the stack it lives in from its own click handler.
            //
            // This covers a button removed on its own. A button whose PARENT is removed gets no
            // event at all, so whoever rebuilds the stack has to release it (see
            // GuiManager.helpMessaging).
            this.on("removed", () => this.releaseMouseControl())
            if (this.onClick) {
                this.on("click", this.onClick)
                this.on("tap", this.onClick)
            }
        }
        this.cursor = "pointer"
        this.y -= padding
    }

    private setMouseControl = (hold: boolean) => {
        if (this.holdingMouseControl === hold) {
            return
        }
        this.holdingMouseControl = hold
        this.mouseControlCallback && this.mouseControlCallback(hold)
    }

    // Give the world its mouse back, if this button is the one holding it. Idempotent, and a no-op
    // for a button that was never hovered.
    releaseMouseControl = () => this.setMouseControl(false)
}
